/**
 * Floating views (extension placement `floating`, SDK 1.10): a button in the page's bottom-right
 * corner instead of a menu item, e.g. "How it works" on a sales demo. Not on the view's own page.
 * The update notice (UpdateNotice.tsx), when it shows, sits above it in the same corner.
 */
import { Link, useLocation } from 'react-router'
import { cx } from '@/kit/cx'

export interface FloatingLink {
    key: string
    label: string
    to: string
}

/** A play mark (Phosphor play, filled) in a 16px frame. */
function PlayMark() {
    return (
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 256 256" fill="currentColor" className="shrink-0">
            <path d="M240 128a15.74 15.74 0 0 1-7.6 13.51L88.32 229.65a16 16 0 0 1-16.2.3A15.86 15.86 0 0 1 64 216.13V39.87a15.86 15.86 0 0 1 8.12-13.82 16 16 0 0 1 16.2.3l144.08 88.14A15.74 15.74 0 0 1 240 128Z" />
        </svg>
    )
}

export function FloatingLinks({ links }: { links: FloatingLink[] }) {
    const location = useLocation()
    const shown = links.filter((link) => location.pathname !== link.to && !location.pathname.startsWith(`${link.to}/`))
    if (shown.length === 0) return null
    return (
        <div className="pointer-events-none fixed right-[24px] bottom-[24px] z-40 flex flex-col items-end gap-[8px] print:hidden max-sm:right-[16px] max-sm:bottom-[16px]">
            {shown.map((link) => (
                <Link
                    key={link.key}
                    to={link.to}
                    data-testid={`floating-${link.key}`}
                    className={cx(
                        'pointer-events-auto inline-flex items-center gap-[8px] rounded-full py-[10px] pr-[18px] pl-[14px] no-underline pointer-coarse:min-h-[var(--size-touch-target)]',
                        '[background:var(--button-primary-fill)] text-[var(--text-on-accent)] shadow-[var(--shadow-popout)]',
                        'transition-transform duration-150 hover:-translate-y-[1px] focus-visible:shadow-[var(--focus-m)] focus-visible:outline-none motion-reduce:transition-none',
                    )}
                >
                    <PlayMark />
                    <span className="ss03 text-[14px] leading-[20px] font-[550] whitespace-nowrap opacity-90">{link.label}</span>
                </Link>
            ))}
        </div>
    )
}
